/**
 * Ableton device runtime state - the PWA-side mirror of `GET /devices/status`
 * on each bridge an Ableton Device control points at.
 *
 * Mirrors `obs/scenes.ts` in shape, and for the same two reasons. Keyed by
 * BRIDGE ID because Ableton is a per-machine program: the Mac's bridge and the
 * music PC's bridge answer about different Live sets, and one global device
 * list would show the wrong machine's state with no way to tell. And a plain
 * signal over an immutable record, never a Map - a Solid read of a Map tracks
 * nothing, and the resulting dep sits forever on a replaced object.
 *
 * # Where the honesty lives
 *
 * The three failure kinds have three different owners, so they stay apart all
 * the way to the button's face:
 *
 * - `unauthorized` - the bearer predates `opbuttons-full` **v16**. The fix is
 *   a RE-PAIR to re-mint, not anything in Ableton, and the operator cannot
 *   guess that from a dark button.
 * - `unavailable` - OPBridge answered, but `OP-ButtonsDevice` has never
 *   spoken (`has_ever_received: false`) or has gone quiet (`stale`). The fix
 *   is in Live: load the device, or find the silently-deaf second instance
 *   that lost the port bind.
 * - `error` - anything else.
 *
 * # `stale` keeps its readings, deliberately
 *
 * OPBridge does not empty the collection when the device goes quiet, and we
 * do not either. An empty list would read as "no such device" when the truth
 * is "nobody is talking", and those send the operator to different places.
 * The status carries the staleness; the readings carry the last known truth.
 */

import { createSignal, type Accessor } from 'solid-js';
import type { DevicesStatusResponse } from '../opbridge/httpClient.ts';
import type {
  AbletonDeviceReading,
  AbletonDeviceState,
  AbletonDeviceStatus,
} from './deviceControl.ts';
import { UNREAD_DEVICE_STATE } from './deviceControl.ts';

/** One bridge's mirror, plus the liveness facts the Inspector explains with. */
export interface BridgeDeviceState extends AbletonDeviceState {
  /** The M4L device is loaded and has not gone quiet. */
  alive: boolean;
  /** Self-reported build from the last `alive`, e.g. `v0.1-rc2`. */
  deviceVersion?: string;
  /** `Date.now()` of the last completed read attempt, 0 if never. */
  at: number;
  /** Human-readable cause, for the dead-state copy. */
  detail?: string;
}

const EMPTY: BridgeDeviceState = {
  ...UNREAD_DEVICE_STATE,
  readings: [],
  alive: false,
  at: 0,
};

const [statesSignal, setStatesSignal] = createSignal<Record<string, BridgeDeviceState>>({});

/** The whole per-bridge mirror. Reactive. */
export const abletonDeviceStates: Accessor<Record<string, BridgeDeviceState>> = statesSignal;

/** One bridge's last-known device state. Never `undefined` - an unread bridge
 *  reads as `status: 'unknown'`, so callers never distinguish "absent key"
 *  from "not asked yet". */
export function abletonDeviceStateFor(bridgeId: string): BridgeDeviceState {
  return statesSignal()[bridgeId] ?? EMPTY;
}

/**
 * Translate one `/devices/status` body into our shape.
 *
 * Exported for the smokes: this is the only place the wire's field names and
 * its `snake_case` resolution spellings exist, and a translation nobody can
 * drive directly is a translation nobody tests.
 *
 * **An unrecognised `state` is DROPPED, not coerced.** Defaulting an unknown
 * verdict to `resolved` would light a button from a value we did not
 * understand, which is the one outcome worth more than a missing row.
 */
export function readingsFromWire(body: DevicesStatusResponse): AbletonDeviceReading[] {
  const out: AbletonDeviceReading[] = [];
  for (const e of body.devices ?? []) {
    const state: AbletonDeviceReading['state'] | null =
      e.state === 'resolved'  ? 'resolved'
      : e.state === 'no_match'  ? 'no-match'
      : e.state === 'ambiguous' ? 'ambiguous'
      : null;
    if (state === null) continue;
    out.push({
      trackName: String(e.track ?? ''),
      deviceName: String(e.name ?? ''),
      nth: typeof e.nth === 'number' && e.nth >= 1 ? e.nth : 1,
      state,
      ...(state === 'resolved' && typeof e.on === 'boolean' ? { on: e.on } : {}),
      ...(state === 'ambiguous' && typeof e.count === 'number' ? { count: e.count } : {}),
    });
  }
  return out;
}

/**
 * Classify a successful read.
 *
 * `has_ever_received` and `stale` answer different questions and produce
 * different copy, which is why OPBridge sends both and why this does not
 * collapse them. Note the ORDER: never-spoken outranks gone-quiet, because a
 * device that has never spoken is also trivially stale, and telling the
 * operator "it went quiet" about a device that was never there would send
 * them looking for a fault that does not exist.
 */
export function statusFromWire(body: DevicesStatusResponse): {
  status: AbletonDeviceStatus;
  detail?: string;
} {
  if (!body.has_ever_received) {
    return {
      status: 'unavailable',
      detail: 'OP-ButtonsDevice has never reported to this bridge - is it loaded in Live?',
    };
  }
  if (body.stale || !body.alive) {
    return {
      status: 'unavailable',
      detail: 'OP-ButtonsDevice has gone quiet. Values below are the last known ones.',
    };
  }
  return { status: 'ok' };
}

/** Record a successful read. */
export function recordDevicesStatus(bridgeId: string, body: DevicesStatusResponse): void {
  const { status, detail } = statusFromWire(body);
  setStatesSignal((prev) => ({
    ...prev,
    [bridgeId]: {
      status,
      // Readings are kept even on `unavailable` - see the file header.
      readings: readingsFromWire(body),
      alive: body.alive === true,
      ...(body.device_version ? { deviceVersion: body.device_version } : {}),
      at: Date.now(),
      ...(detail ? { detail } : {}),
    },
  }));
}

/** Record a failed read, preserving WHICH failure. Readings are cleared: a
 *  read we could not complete says nothing about what is still true, and
 *  keeping them would let a button stay lit from a state we can no longer
 *  confirm. That is the opposite tradeoff from `stale` above, deliberately -
 *  there OPBridge answered and vouched for the values. */
export function recordDevicesFailure(
  bridgeId: string,
  status: Exclude<AbletonDeviceStatus, 'unknown' | 'ok'>,
  detail: string,
): void {
  setStatesSignal((prev) => ({
    ...prev,
    [bridgeId]: { status, readings: [], alive: false, at: Date.now(), detail },
  }));
}

/** Drop every mirror. Tests + sign-out. */
export function resetAbletonDeviceStates(): void {
  setStatesSignal({});
}
